// ModuleManager.jsx - Version avec DemoViewer en modal - Tailwind CSS
// Gère les types PDF, VIDEO, JSON, JSON_RICH, EVALUATION
import React, { useState, useEffect, useCallback } from 'react';
import { APP_CONFIG } from '../../config/appConfig';
import ModuleContentManager from './ModuleContentManager';
import ConstruireFormationJsonAdmin from './ConstruireJsonAdmin';
import ConstruireJsonRichAdmin from './ConstruireJsonRichAdmin';
import ConstruireEvaluation from './ConstruireEvaluation';
import { BarChart3, BookOpen, FileText, Plus, Trash2, Edit, Eye, Link, ChevronUp, ChevronDown, Video } from 'lucide-react';
import DemoViewer from '../../components/communs/DemoViewer';

export default function ModuleManager({ formationId, clientId, token, onModuleChange }) {
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modals
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [showContentModal, setShowContentModal] = useState(false);
    const [selectedModule, setSelectedModule] = useState(null);
    const [showEvaluationModal, setShowEvaluationModal] = useState(false);
    const [evalModule, setEvalModule] = useState(null);
    const [showJsonBuilderModal, setShowJsonBuilderModal] = useState(false);
    const [jsonModule, setJsonModule] = useState(null);
    const [showJsonRichBuilderModal, setShowJsonRichBuilderModal] = useState(false);
    const [jsonRichModule, setJsonRichModule] = useState(null);

    const [showModuleDemo, setShowModuleDemo] = useState(false);
    const [demoModuleId, setDemoModuleId] = useState(null);
    const [demoModuleData, setDemoModuleData] = useState(null);

    const [formData, setFormData] = useState({
        titre: '',
        ordre: 0,
        dureeMaxMinutes: 60,
        typeModule: 'PDF'
    });

    const fetchModules = useCallback(async () => {
        if (!token || !formationId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/formations/${formationId}/modules`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}`);
            }

            const data = await response.json();
            console.log('📦 [MODULE MANAGER] Modules récupérés:', data);
            setModules(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ [MODULE MANAGER] Erreur récupération modules:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token, formationId]);

    useEffect(() => {
        fetchModules();
    }, [fetchModules]);

    const getModuleType = (module) => {
        return module.typeModule || 'PDF';
    };

    const hasPdfContent = (module) => {
        return module.pdfStreamUrl && module.pdfStreamUrl.length > 0;
    };

    const hasVideoContent = (module) => {
        return module.videoUrl && module.videoUrl.length > 0;
    };

    const hasJsonContent = (module) => {
        return module.jsonContent && Object.keys(module.jsonContent).length > 0;
    };

    const hasEvaluationLinked = (module) => {
        return module.evaluationId !== null &&
               module.evaluationId !== undefined &&
               module.evaluationId !== 0;
    };

    const handleOpenDemo = async (module) => {
        console.log('🔍 [MODULE MANAGER] handleOpenDemo appelé');
        console.log('🔍 [MODULE MANAGER] Module:', module);

        if (!module) {
            console.error('❌ [MODULE MANAGER] Module manquant');
            alert("❌ Informations du module manquantes");
            return;
        }

        const moduleType = getModuleType(module);
        let hasContentFlag = false;

        switch (moduleType) {
            case 'PDF':
                hasContentFlag = hasPdfContent(module);
                break;
            case 'VIDEO':
                hasContentFlag = hasVideoContent(module);
                break;
            case 'JSON':
            case 'JSON_RICH':
                hasContentFlag = hasJsonContent(module);
                break;
            case 'EVALUATION':
                hasContentFlag = hasEvaluationLinked(module);
                break;
            default:
                hasContentFlag = false;
        }

        if (!hasContentFlag) {
            alert(`❌ Ce module de type ${moduleType} n'a pas encore de contenu`);
            return;
        }

        try {
            let jsonContent = null;
            let evaluationData = null;
            let pdfUrl = module.pdfStreamUrl || null;
            let videoUrl = null;
            let videoToken = null;
            let contentType = moduleType;

            console.log(`🔍 [MODULE MANAGER] Type de module: ${contentType}`);

            // ⭐ Pour VIDEO, générer le token
            if (contentType === 'VIDEO' && module.videoUrl) {
                try {
                    const tokenResponse = await fetch(
                        `${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${module.id}/video/token`,
                        {
                            method: 'POST',
                            headers: {
                                "Authorization": `Bearer ${token}`,
                                "Accept": "application/json"
                            }
                        }
                    );
                    if (tokenResponse.ok) {
                        const tokenData = await tokenResponse.json();
                        videoToken = tokenData.token;
                        videoUrl = `/api/v1/admin/modules/${module.id}/video/stream?token=${videoToken}`;
                        console.log('🎬 [MODULE MANAGER] Token vidéo généré:', videoToken);
                    } else {
                        // Fallback: utiliser l'URL sans token
                        videoUrl = `/api/v1/modules/${module.id}/video/stream`;
                    }
                } catch (e) {
                    console.warn("⚠️ [MODULE MANAGER] Erreur génération token vidéo:", e);
                    videoUrl = `/api/v1/modules/${module.id}/video/stream`;
                }
            }

            if (contentType === 'JSON' || contentType === 'JSON_RICH' || contentType === 'EVALUATION') {
                try {
                    const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${module.id}/json-content`, {
                        headers: {
                            "Authorization": `Bearer ${token}`,
                            "Accept": "application/json"
                        }
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data) {
                            jsonContent = data;
                            if (contentType === 'EVALUATION' || (Array.isArray(data) && data[0]?.enonce)) {
                                contentType = 'EVALUATION';
                                evaluationData = data;
                            }
                        }
                    }
                } catch (e) {
                    console.warn("⚠️ [MODULE MANAGER] Pas de contenu JSON:", e);
                }
            }

            const demoData = {
                formationId: formationId,
                module: {
                    id: module.id,
                    titre: module.titre || 'Module sans titre',
                    description: module.description || '',
                    type: module.typeModule || 'FORMATION',
                    ordre: module.ordre || 0,
                    formationId: module.formationId || formationId,
                    pdfStreamUrl: pdfUrl,
                    videoUrl: videoUrl,
                    videoToken: videoToken
                },
                content: {
                    type: contentType,
                    data: contentType === 'PDF' ? pdfUrl : (contentType === 'VIDEO' ? videoUrl : (evaluationData || jsonContent || {})),
                    pdfUrl: pdfUrl,
                    videoUrl: videoUrl,
                    videoToken: videoToken,
                    jsonContent: jsonContent ? JSON.stringify(jsonContent, null, 2) : null,
                    evaluationData: evaluationData
                },
                token: token,
                mode: 'demo'
            };

            console.log('🚀 [MODULE MANAGER] Ouverture DemoViewer pour le module:', module.id);

            setDemoModuleId(module.id);
            setDemoModuleData(demoData);
            setShowModuleDemo(true);

        } catch (err) {
            console.error("❌ [MODULE MANAGER] Erreur ouverture démo:", err);
            alert("❌ Erreur lors de l'ouverture de la démo: " + err.message);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                titre: formData.titre,
                ordre: parseInt(formData.ordre) || 0,
                dureeMaxMinutes: parseInt(formData.dureeMaxMinutes) || 60,
                typeModule: formData.typeModule
            };
            console.log("📤 [MODULE MANAGER] Payload envoyé:", payload);

            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/formations/${formationId}/modules`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                setShowModal(false);
                resetForm();
                await fetchModules();
                if (onModuleChange) onModuleChange();

                console.log("✅ [MODULE MANAGER] Module créé avec succès, ID:", data.id);

                const newModule = modules.find(m => m.id === data.id);
                const moduleToUse = newModule || data;

                switch (formData.typeModule) {
                    case 'JSON':
                        console.log("📝 [MODULE MANAGER] Ouverture automatique du constructeur JSON");
                        setJsonModule(moduleToUse);
                        setShowJsonBuilderModal(true);
                        break;
                    case 'JSON_RICH':
                        console.log("📝 [MODULE MANAGER] Ouverture automatique du constructeur JSON_RICH");
                        setJsonRichModule(moduleToUse);
                        setShowJsonRichBuilderModal(true);
                        break;
                    case 'EVALUATION':
                        console.log("📊 [MODULE MANAGER] Ouverture automatique de l'éditeur d'évaluation");
                        setEvalModule(moduleToUse);
                        setShowEvaluationModal(true);
                        break;
                    case 'PDF':
                        console.log("📄 [MODULE MANAGER] Ouverture automatique du gestionnaire PDF");
                        setSelectedModule(moduleToUse);
                        setShowContentModal(true);
                        break;
                    case 'VIDEO':
                        console.log("🎬 [MODULE MANAGER] Ouverture automatique du gestionnaire Vidéo");
                        setSelectedModule(moduleToUse);
                        setShowContentModal(true);
                        break;
                    default:
                        break;
                }

                alert("✅ Module créé avec succès");
            } else {
                setError(data.error || "Erreur lors de la création");
                alert(`❌ ${data.error || "Erreur lors de la création"}`);
            }
        } catch (err) {
            console.error("❌ [MODULE MANAGER] Erreur création module:", err);
            setError("Erreur technique lors de la création");
            alert("❌ Erreur technique lors de la création");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                titre: formData.titre,
                ordre: parseInt(formData.ordre) || 0,
                dureeMaxMinutes: parseInt(formData.dureeMaxMinutes) || 60,
                typeModule: formData.typeModule
            };

            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${editingModule.id}`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                setShowEditModal(false);
                setEditingModule(null);
                resetForm();
                await fetchModules();
                if (onModuleChange) onModuleChange();
                alert("✅ Module modifié avec succès");
            } else {
                setError(data.error || "Erreur lors de la modification");
                alert(`❌ ${data.error || "Erreur lors de la modification"}`);
            }
        } catch (err) {
            console.error("❌ [MODULE MANAGER] Erreur modification module:", err);
            setError("Erreur technique lors de la modification");
            alert("❌ Erreur technique lors de la modification");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (module) => {
        if (!window.confirm(`⚠️ Êtes-vous sûr de vouloir supprimer le module "${module.titre}" ?`)) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${module.id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (response.ok) {
                await fetchModules();
                if (onModuleChange) onModuleChange();
                alert("✅ Module supprimé avec succès");
            } else {
                const data = await response.json();
                setError(data.error || "Erreur lors de la suppression");
                alert(`❌ ${data.error || "Erreur lors de la suppression"}`);
            }
        } catch (err) {
            console.error("❌ [MODULE MANAGER] Erreur suppression module:", err);
            setError("Erreur technique lors de la suppression");
            alert("❌ Erreur technique lors de la suppression");
        } finally {
            setLoading(false);
        }
    };

    const handleReorder = async (moduleId, newOrdre) => {
        if (newOrdre < 0) return;

        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${moduleId}/reorder?newOrdre=${newOrdre}`,
                {
                    method: "PATCH",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (response.ok) {
                await fetchModules();
            }
        } catch (err) {
            console.error("❌ [MODULE MANAGER] Erreur réordonnancement:", err);
        }
    };

    const openEditModal = (module) => {
        setEditingModule(module);
        setFormData({
            titre: module.titre || '',
            ordre: module.ordre || 0,
            dureeMaxMinutes: module.dureeMaxMinutes || 60,
            typeModule: module.typeModule || 'PDF'
        });
        setShowEditModal(true);
    };

    const openContentModal = (module) => {
        setSelectedModule(module);
        setShowContentModal(true);
    };

    const openEvaluationModal = (module) => {
        setEvalModule(module);
        setShowEvaluationModal(true);
    };

    const openJsonBuilderModal = (module) => {
        setJsonModule(module);
        setShowJsonBuilderModal(true);
    };

    const openJsonRichBuilderModal = (module) => {
        setJsonRichModule(module);
        setShowJsonRichBuilderModal(true);
    };

    const resetForm = () => {
        setFormData({
            titre: '',
            ordre: modules.length + 1,
            dureeMaxMinutes: 60,
            typeModule: 'PDF'
        });
        setError(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseInt(value) || 0 : value
        }));
    };

    const getModuleTypeBadge = (module) => {
        const type = getModuleType(module);
        switch (type) {
            case 'PDF':
                return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">📄 PDF</span>;
            case 'VIDEO':
                return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800">🎬 Vidéo</span>;
            case 'JSON':
                return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">📝 JSON</span>;
            case 'JSON_RICH':
                return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">📝 JSON_RICH</span>;
            case 'EVALUATION':
                return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">📊 Évaluation</span>;
            default:
                return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{type}</span>;
        }
    };

    const handleEvaluationSave = useCallback(async (savedEvaluation) => {
        try {
            console.log('📊 [MODULE MANAGER] Sauvegarde de l\'évaluation:', savedEvaluation);
            console.log('📊 [MODULE MANAGER] Liaison au module:', evalModule.id);

            const linkResponse = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/admin/evaluations/${savedEvaluation.id}/link-module/${evalModule.id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!linkResponse.ok) {
                const errorData = await linkResponse.json();
                throw new Error(errorData.error || `Erreur HTTP ${linkResponse.status}`);
            }

            const linkedEvaluation = await linkResponse.json();
            console.log('✅ [MODULE MANAGER] Évaluation liée avec succès:', linkedEvaluation);

            await fetchModules();
            setShowEvaluationModal(false);
            setEvalModule(null);

            alert('✅ Évaluation liée au module avec succès');
        } catch (err) {
            console.error('❌ [MODULE MANAGER] Erreur liaison évaluation:', err);
            alert(`❌ Erreur lors de la liaison de l'évaluation: ${err.message}`);
        }
    }, [token, evalModule, fetchModules]);

    const handleJsonSave = useCallback(async (jsonString) => {
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${jsonModule.id}/json-content?typeModule=JSON`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: jsonString
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}`);
            }

            console.log('✅ JSON sauvegardé');
            await fetchModules();
            setShowJsonBuilderModal(false);
            alert('✅ Contenu JSON sauvegardé avec succès');
        } catch (err) {
            console.error('❌ Erreur sauvegarde JSON:', err);
            alert(`❌ Erreur lors de la sauvegarde du JSON: ${err.message}`);
        }
    }, [token, jsonModule, fetchModules]);

    // Sauvegarde pour JSON_RICH (utilisée uniquement si on passe par le bouton "Sauvegarder" du constructeur)
    // Mais ConstruireJsonRichAdmin fait lui-même l'appel avec ?typeModule=JSON_RICH, donc on peut laisser vide.
    // On peut définir une fonction similaire si besoin, mais laissons le composant gérer.

    const handleJsonPreview = useCallback((data) => {
        const previewData = {
            formationId: formationId,
            module: {
                id: jsonModule.id,
                titre: jsonModule.titre || 'Formation JSON',
                description: jsonModule.description || '',
                type: 'JSON',
                ordre: jsonModule.ordre || 0,
                formationId: formationId
            },
            content: {
                type: 'JSON',
                data: data
            },
            token: token,
            mode: 'demo'
        };

        setDemoModuleId(jsonModule.id);
        setDemoModuleData(previewData);
        setShowModuleDemo(true);
    }, [jsonModule, formationId, token]);

    const handleJsonRichPreview = useCallback((data) => {
        const previewData = {
            formationId: formationId,
            module: {
                id: jsonRichModule.id,
                titre: jsonRichModule.titre || 'Formation JSON_RICH',
                description: jsonRichModule.description || '',
                type: 'JSON_RICH',
                ordre: jsonRichModule.ordre || 0,
                formationId: formationId
            },
            content: {
                type: 'JSON_RICH',
                data: data
            },
            token: token,
            mode: 'demo'
        };

        setDemoModuleId(jsonRichModule.id);
        setDemoModuleData(previewData);
        setShowModuleDemo(true);
    }, [jsonRichModule, formationId, token]);

    if (loading && modules.length === 0) {
        return (
            <div className="text-center py-8">
                <div className="flex justify-center">
                    <svg className="animate-spin h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
                <p className="text-xs text-gray-500 mt-2">Chargement des modules...</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Bouton ajouter un module */}
            <div className="flex justify-end">
                <button
                    onClick={() => { resetForm(); setShowModal(true); }}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-1"
                >
                    <Plus size={16} />
                    Ajouter un module
                </button>
            </div>

            {/* Liste des modules */}
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    <strong>Erreur :</strong> {error}
                </div>
            )}

            {modules.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-sm text-gray-400">Aucun module pour cette formation</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {modules
                        .sort((a, b) => (a.ordre || 0) - (b.ordre || 0))
                        .map((module, index) => {
                            const moduleType = getModuleType(module);
                            const hasEval = hasEvaluationLinked(module);
                            const hasPdf = hasPdfContent(module);
                            const hasVideo = hasVideoContent(module);
                            const hasJson = hasJsonContent(module);

                            const canShowDemo =
                                (moduleType === 'PDF' && hasPdf) ||
                                (moduleType === 'VIDEO' && hasVideo) ||
                                ((moduleType === 'JSON' || moduleType === 'JSON_RICH') && hasJson) ||
                                (moduleType === 'EVALUATION' && hasEval);

                            return (
                                <div key={module.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 hover:border-green-300 transition-colors">
                                    {/* Ordre */}
                                    <div className="flex flex-col items-center gap-0.5">
                                        <button
                                            onClick={() => handleReorder(module.id, (module.ordre || 0) - 1)}
                                            disabled={index === 0}
                                            className="p-0.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                            title="Monter"
                                        >
                                            <ChevronUp size={16} />
                                        </button>
                                        <span className="text-xs font-bold text-gray-400 w-6 text-center">{module.ordre || index + 1}</span>
                                        <button
                                            onClick={() => handleReorder(module.id, (module.ordre || 0) + 1)}
                                            disabled={index === modules.length - 1}
                                            className="p-0.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                            title="Descendre"
                                        >
                                            <ChevronDown size={16} />
                                        </button>
                                    </div>

                                    {/* Informations */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h4 className="font-semibold text-gray-900">{module.titre}</h4>
                                            {getModuleTypeBadge(module)}
                                            {module.dureeMaxMinutes > 0 && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                    ⏱ {module.dureeMaxMinutes} min
                                                </span>
                                            )}
                                            {moduleType === 'EVALUATION' && hasEval && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                                    🔗 Évaluation liée (ID: {module.evaluationId})
                                                </span>
                                            )}
                                            {moduleType === 'EVALUATION' && !hasEval && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                                    ⚠️ Aucune évaluation
                                                </span>
                                            )}
                                            {moduleType === 'PDF' && hasPdf && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                                    📄 PDF présent
                                                </span>
                                            )}
                                            {moduleType === 'PDF' && !hasPdf && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                                    ⚠️ PDF manquant
                                                </span>
                                            )}
                                            {moduleType === 'VIDEO' && hasVideo && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
                                                    🎬 Vidéo présente
                                                </span>
                                            )}
                                            {moduleType === 'VIDEO' && !hasVideo && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                                    ⚠️ Vidéo manquante
                                                </span>
                                            )}
                                            {(moduleType === 'JSON' || moduleType === 'JSON_RICH') && !hasJson && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                                    ⚠️ JSON en attente
                                                </span>
                                            )}
                                            {(moduleType === 'JSON' || moduleType === 'JSON_RICH') && hasJson && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                                    ✅ JSON présent
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            {moduleType === 'PDF' && (hasPdf ? 'PDF disponible' : 'Aucun PDF uploadé')}
                                            {moduleType === 'VIDEO' && (hasVideo ? 'Vidéo disponible' : 'Aucune vidéo uploadée')}
                                            {(moduleType === 'JSON' || moduleType === 'JSON_RICH') && (hasJson ? 'Contenu JSON structuré' : 'Aucun contenu JSON')}
                                            {moduleType === 'EVALUATION' && (hasEval ? `Évaluation liée (ID: ${module.evaluationId})` : 'Aucune évaluation liée')}
                                        </p>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex gap-1 flex-shrink-0 flex-wrap">
                                        {/* Eye - Voir en démo */}
                                        {canShowDemo && (
                                            <button
                                                onClick={() => handleOpenDemo(module)}
                                                className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                                                title="Voir en démo"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        )}

                                        {/* BookOpen - Constructeur JSON ou JSON_RICH */}
                                        {(moduleType === 'JSON' || moduleType === 'JSON_RICH') && (
                                            <button
                                                onClick={() => {
                                                    if (moduleType === 'JSON_RICH') {
                                                        openJsonRichBuilderModal(module);
                                                    } else {
                                                        openJsonBuilderModal(module);
                                                    }
                                                }}
                                                className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                                                title={hasJson ? "Modifier le JSON" : "Construire le JSON"}
                                            >
                                                <BookOpen size={16} />
                                            </button>
                                        )}

                                        {/* FileText - Contenu PDF/VIDEO/JSON simple */}
                                        {moduleType === 'PDF' && (
                                            <button
                                                onClick={() => openContentModal(module)}
                                                className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                                                title={hasPdf ? "Gérer le PDF" : "Ajouter un PDF"}
                                            >
                                                <FileText size={16} />
                                            </button>
                                        )}
                                        {moduleType === 'VIDEO' && (
                                            <button
                                                onClick={() => openContentModal(module)}
                                                className="p-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors"
                                                title={hasVideo ? "Gérer la vidéo" : "Ajouter une vidéo"}
                                            >
                                                <Video size={16} />
                                            </button>
                                        )}
                                        {moduleType === 'JSON' && hasJson && (
                                            <button
                                                onClick={() => openContentModal(module)}
                                                className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Voir le contenu JSON"
                                            >
                                                <FileText size={16} />
                                            </button>
                                        )}
                                        {moduleType === 'JSON_RICH' && hasJson && (
                                            <button
                                                onClick={() => openContentModal(module)}
                                                className="p-1.5 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                                                title="Voir le contenu JSON_RICH"
                                            >
                                                <FileText size={16} />
                                            </button>
                                        )}

                                        {/* Link - Évaluation */}
                                        {moduleType === 'EVALUATION' && (
                                            <button
                                                onClick={() => openEvaluationModal(module)}
                                                className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                                                title={hasEval ? "Voir l'évaluation liée" : "Ajouter une évaluation"}
                                            >
                                                <Link size={16} />
                                            </button>
                                        )}

                                        {/* Edit - Modifier */}
                                        <button
                                            onClick={() => openEditModal(module)}
                                            className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                            title="Modifier"
                                        >
                                            <Edit size={16} />
                                        </button>

                                        {/* Trash2 - Supprimer */}
                                        <button
                                            onClick={() => handleDelete(module)}
                                            className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Supprimer"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : CRÉATION D'UN MODULE */}
            {/* ======================================== */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">✦</span>
                                Nouveau module
                            </h3>
                            <button
                                onClick={() => { setShowModal(false); resetForm(); }}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleCreate} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                                <input
                                    type="text"
                                    name="titre"
                                    placeholder="Titre du module"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    value={formData.titre}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Ordre</label>
                                    <input
                                        type="number"
                                        name="ordre"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.ordre}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Durée max (minutes)</label>
                                    <input
                                        type="number"
                                        name="dureeMaxMinutes"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.dureeMaxMinutes}
                                        onChange={handleInputChange}
                                        min="1"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type de module *</label>
                                <select
                                    name="typeModule"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    value={formData.typeModule}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="PDF">📄 PDF - Upload de document PDF</option>
                                    <option value="VIDEO">🎬 Vidéo - Upload d'un fichier vidéo (mp4/webm)</option>
                                    <option value="JSON">📝 JSON - Formation structurée (constructeur simple)</option>
                                    <option value="JSON_RICH">📝 JSON_RICH - Construction enrichie (médias)</option>
                                    <option value="EVALUATION">📊 Évaluation - QCM, Diagnostic, Sommative</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    {formData.typeModule === 'PDF'
                                        ? '💡 Le gestionnaire PDF s\'ouvrira automatiquement après la création.'
                                        : formData.typeModule === 'VIDEO'
                                        ? '💡 Le gestionnaire vidéo s\'ouvrira automatiquement après la création.'
                                        : formData.typeModule === 'JSON'
                                        ? '💡 Le constructeur JSON s\'ouvrira automatiquement après la création.'
                                        : formData.typeModule === 'JSON_RICH'
                                        ? '💡 Le constructeur JSON_RICH s\'ouvrira automatiquement après la création.'
                                        : '💡 L\'éditeur d\'évaluation s\'ouvrira automatiquement après la création.'}
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? 'Création...' : 'Créer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : MODIFICATION D'UN MODULE */}
            {/* ======================================== */}
            {showEditModal && editingModule && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">✎</span>
                                Modifier le module
                            </h3>
                            <button
                                onClick={() => { setShowEditModal(false); setEditingModule(null); resetForm(); }}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleUpdate} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                                <input
                                    type="text"
                                    name="titre"
                                    placeholder="Titre du module"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    value={formData.titre || ''}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Ordre</label>
                                    <input
                                        type="number"
                                        name="ordre"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.ordre || 0}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Durée max (minutes)</label>
                                    <input
                                        type="number"
                                        name="dureeMaxMinutes"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.dureeMaxMinutes || 60}
                                        onChange={handleInputChange}
                                        min="1"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type de module</label>
                                <select
                                    name="typeModule"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    value={formData.typeModule || 'PDF'}
                                    onChange={handleInputChange}
                                >
                                    <option value="PDF">📄 PDF - Upload de document PDF</option>
                                    <option value="VIDEO">🎬 Vidéo - Upload d'un fichier vidéo (mp4/webm)</option>
                                    <option value="JSON">📝 JSON - Formation structurée (constructeur simple)</option>
                                    <option value="JSON_RICH">📝 JSON_RICH - Construction enrichie (médias)</option>
                                    <option value="EVALUATION">📊 Évaluation - QCM, Diagnostic, Sommative</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    {formData.typeModule === 'PDF'
                                        ? '💡 Gérez le PDF dans la section "Contenu" du module.'
                                        : formData.typeModule === 'VIDEO'
                                        ? '💡 Gérez la vidéo dans la section "Contenu" du module.'
                                        : formData.typeModule === 'JSON'
                                        ? '💡 Utilisez le constructeur JSON pour créer la structure.'
                                        : formData.typeModule === 'JSON_RICH'
                                        ? '💡 Utilisez le constructeur JSON_RICH pour créer une structure enrichie avec médias.'
                                        : '💡 Créez ou liez une évaluation au module.'}
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => { setShowEditModal(false); setEditingModule(null); resetForm(); }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? 'Mise à jour...' : 'Mettre à jour'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : GESTION DU CONTENU */}
            {/* ======================================== */}
            {showContentModal && selectedModule && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">
                                    {selectedModule.typeModule === 'PDF' ? '📄' : selectedModule.typeModule === 'VIDEO' ? '🎬' : selectedModule.typeModule === 'JSON_RICH' ? '📝' : '📝'}
                                </span>
                                Contenu de : {selectedModule.titre}
                            </h3>
                            <button
                                onClick={() => { setShowContentModal(false); setSelectedModule(null); }}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-6 max-h-[60vh] overflow-y-auto">
                            <ModuleContentManager
                                moduleId={selectedModule.id}
                                token={token}
                                initialType={selectedModule.typeModule === 'PDF' ? 'PDF' : selectedModule.typeModule === 'VIDEO' ? 'VIDEO' : selectedModule.typeModule === 'JSON_RICH' ? 'JSON_RICH' : 'JSON'}
                                initialPdfUrl={selectedModule.pdfStreamUrl}
                                initialVideoUrl={selectedModule.videoUrl}
                                initialJsonContent={selectedModule.jsonContent ? JSON.stringify(selectedModule.jsonContent, null, 2) : ''}
                                onContentUpdate={fetchModules}
                                module={selectedModule}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : CONSTRUCTEUR JSON */}
            {/* ======================================== */}
            {showJsonBuilderModal && jsonModule && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">📝</span>
                                Constructeur JSON - {jsonModule.titre}
                            </h3>
                            <button
                                onClick={() => { setShowJsonBuilderModal(false); setJsonModule(null); }}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
                            <ConstruireFormationJsonAdmin
                                initialJson={jsonModule.jsonContent}
                                onSave={handleJsonSave}
                                onPreview={handleJsonPreview}
                                token={token}
                                moduleId={jsonModule.id}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : CONSTRUCTEUR JSON_RICH */}
            {/* ======================================== */}
            {showJsonRichBuilderModal && jsonRichModule && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">📝</span>
                                Constructeur JSON_RICH - {jsonRichModule.titre}
                            </h3>
                            <button
                                onClick={() => { setShowJsonRichBuilderModal(false); setJsonRichModule(null); }}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
                            <ConstruireJsonRichAdmin
                                initialJson={jsonRichModule.jsonContent}
                                // onSave n'est pas nécessaire car le composant gère lui-même la sauvegarde avec ?typeModule=JSON_RICH
                                onPreview={handleJsonRichPreview}
                                token={token}
                                moduleId={jsonRichModule.id}
                                apiBase={APP_CONFIG.BASE_URL}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : GESTION DES ÉVALUATIONS */}
            {/* ======================================== */}
            {showEvaluationModal && evalModule && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">📊</span>
                                {hasEvaluationLinked(evalModule) ? `Évaluation liée (ID: ${evalModule.evaluationId})` : `Évaluation de : ${evalModule.titre}`}
                            </h3>
                            <button
                                onClick={() => { setShowEvaluationModal(false); setEvalModule(null); }}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
                            <ConstruireEvaluation
                                evaluationId={evalModule.evaluationId}
                                clientId={clientId}
                                token={token}
                                evalType="SOMMATIVE"
                                onSave={handleEvaluationSave}
                                onPreview={(data) => {
                                    const previewData = {
                                        formationId: formationId,
                                        module: {
                                            id: evalModule.id,
                                            titre: evalModule.titre || 'Évaluation',
                                            description: evalModule.description || '',
                                            type: 'EVALUATION',
                                            ordre: evalModule.ordre || 0,
                                            formationId: formationId
                                        },
                                        content: {
                                            type: 'EVALUATION',
                                            data: data
                                        },
                                        token: token,
                                        mode: 'demo'
                                    };

                                    setDemoModuleId(evalModule.id);
                                    setDemoModuleData(previewData);
                                    setShowModuleDemo(true);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : DÉMONSTRATION D'UN MODULE (DemoViewer) */}
            {/* ======================================== */}
            {showModuleDemo && demoModuleId && demoModuleData && (
                <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4">
                    <div className="w-full max-w-6xl h-[95vh] bg-white rounded-2xl overflow-hidden">
                        <DemoViewer
                            formationId={demoModuleData.formationId || formationId}
                            token={demoModuleData.token || token}
                            onClose={() => {
                                setShowModuleDemo(false);
                                setDemoModuleId(null);
                                setDemoModuleData(null);
                            }}
                            isOpen={showModuleDemo}
                            userRole="admin"
                            initialModule={demoModuleData.module}
                            initialContent={demoModuleData.content}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}